import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { LayoutDashboard, Globe, PlusCircle, Coins, Users, BarChart3, Settings, Eye, CheckCircle2, TrendingUp } from "lucide-react";
import { LogoMark } from "./Logo";
import { EASE } from "./Reveal";

const MENU = [[LayoutDashboard, "Dashboard"], [Globe, "View Sites"], [PlusCircle, "Add Site"], [Coins, "Manage Credits"], [Users, "Members"], [BarChart3, "Reports"], [Settings, "Settings"]];
const SITES = [["yourwebsite.com", 12450], ["example.com", 10230], ["mysite.com", 8900], ["bestsite.com", 7560]];
const ACTIVITY = ["New member registered", "Site added for exchange", "Credits purchased", "Banner view completed"];
const PATH = "M0 70 L30 58 L60 64 L90 44 L120 52 L150 36 L180 42 L210 24 L240 30 L270 12 L300 6";

const useLive = () => {
  const [n, setN] = useState({ m: 2548, c: 1236780, v: 845320 });
  useEffect(() => {
    const t = setInterval(() => setN((p) => ({ m: p.m + (Math.random() > 0.6 ? 1 : 0), c: p.c + Math.floor(Math.random() * 40), v: p.v + Math.floor(Math.random() * 25) })), 1400);
    return () => clearInterval(t);
  }, []);
  return n;
};

const Stat = ({ label, value, color, Icon }) => (
  <div className={`rounded-xl p-3 ${color}`}>
    <p className="text-[10px] font-semibold text-white/85">{label}</p>
    <div className="mt-1 flex items-center justify-between">
      <span className="font-mono-x text-sm font-bold text-white sm:text-base">{value.toLocaleString()}</span>
      <Icon className="h-4 w-4 text-white/90" />
    </div>
  </div>
);

const Sidebar = () => (
  <aside className="hidden w-36 shrink-0 flex-col gap-1 border-r border-slate-200 bg-slate-50 p-3 sm:flex">
    {MENU.map(([I, l], i) => (
      <div key={l} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] font-semibold ${i === 0 ? "bg-blue-600 text-white" : "text-slate-600"}`}>
        <I className="h-3.5 w-3.5" /> {l}
      </div>
    ))}
  </aside>
);

const Chart = () => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex items-center justify-between"><p className="text-[11px] font-bold text-slate-900">Website Traffic</p><span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400"><TrendingUp className="h-3 w-3" />+29.7%</span></div>
    <svg viewBox="0 0 300 80" className="mt-2 h-20 w-full">
      <defs><linearGradient id="dm-f" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2563EB" stopOpacity=".35" /><stop offset="1" stopColor="#2563EB" stopOpacity="0" /></linearGradient></defs>
      <path d={`${PATH} L300 80 L0 80 Z`} fill="url(#dm-f)" />
      <motion.path d={PATH} fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.2, ease: EASE, delay: 0.8 }} />
    </svg>
  </div>
);

const Lists = () => (
  <div className="grid grid-cols-2 gap-3">
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="mb-2 text-[11px] font-bold text-slate-900">Top Websites</p>
      {SITES.map(([s, v]) => (<div key={s} className="flex justify-between py-0.5 text-[10px] text-slate-600"><span>{s}</span><span className="font-mono-x">{v.toLocaleString()}</span></div>))}
    </div>
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="mb-2 text-[11px] font-bold text-slate-900">Recent Activity</p>
      {ACTIVITY.map((a) => (<div key={a} className="flex items-center gap-1.5 py-0.5 text-[10px] text-slate-600"><CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-400" /><span className="truncate">{a}</span></div>))}
    </div>
  </div>
);

export const DashboardMock = ({ children }) => {
  const n = useLive();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [16, 2]), { stiffness: 110, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-20, -2]), { stiffness: 110, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="relative" style={{ perspective: 1400 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} data-testid="hero-dashboard-mockup">
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_40px_120px_-30px_rgba(37,99,235,0.5)]">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
            <div className="flex items-center gap-2"><LogoMark className="h-5 w-5" /><span className="font-display text-xs font-bold text-slate-900">Start-A-TE</span></div>
            <div className="flex gap-1.5"><span className="h-2 w-2 rounded-full bg-red-400" /><span className="h-2 w-2 rounded-full bg-yellow-400" /><span className="h-2 w-2 rounded-full bg-emerald-400" /></div>
          </div>
          <div className="flex">
            <Sidebar />
            <div className="flex-1 space-y-3 p-3">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Total Members" value={n.m} color="bg-blue-600" Icon={Users} />
                <Stat label="Total Credits" value={n.c} color="bg-emerald-600" Icon={Coins} />
                <Stat label="Total Views" value={n.v} color="bg-orange-500" Icon={Eye} />
              </div>
              <Chart />
              <Lists />
            </div>
          </div>
        </div>
        {children}
      </motion.div>
    </div>
  );
};
